import { Divider, Theme, useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import DashboardIcon from "@material-ui/icons/Dashboard";
import {
  default as DefaultIcon,
  default as LabelIcon,
} from "@material-ui/icons/ViewList";
import classnames from "classnames";
import inflection from "inflection";
import lodashGet from "lodash/get";
import { getResources, ReduxState, Translate } from "ra-core";
import React, { FC } from "react";
import { MenuItemLink, useTranslate } from "react-admin";
import { shallowEqual, useSelector } from "react-redux";
import useUserContext from "../../shared/useUserContext";
import { IMenuLink, MenuProps } from "../../types";
import MenuItemExtLink from "./MenuItemExtLink";

export const MENU_WIDTH = 240;
export const CLOSED_MENU_WIDTH = 55;

const useStyles = makeStyles(
  (theme) => ({
    main: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      marginTop: "0.5em",
      [theme.breakpoints.only("xs")]: {
        marginTop: 0,
      },
      [theme.breakpoints.up("md")]: {
        marginTop: "1.5em",
      },
    },
    open: {
      width: lodashGet(theme, "menu.width", MENU_WIDTH),
    },
    closed: {
      width: lodashGet(theme, "menu.closedWidth", CLOSED_MENU_WIDTH),
    },
    dividerFullWidth: {
      margin: `5px 0 0 ${theme.spacing(1)}px`,
    },
    dividerInset: {
      margin: `5px 0 0 ${theme.spacing(2)}px`,
    },
    menuSectionHeader: {
      margin: `10px 0 0 ${theme.spacing(2)}px`,
    },
  }),
  { name: "RaMenu" }
);

const translatedResourceName = (resource: any, translate: Translate) =>
  translate(`resources.${resource.name}.name`, {
    smart_count: 2,
    _: resource?.options?.label
      ? translate(resource.options.label, {
          smart_count: 2,
          _: resource.options.label,
        })
      : inflection.humanize(inflection.pluralize(resource.name)),
  });

const ExternalLink = ({ link, open, ...props }) => {
  const { classes: classesOverride, dense, onMenuClick } = props;

  return (
    <MenuItemExtLink
      key={link.name}
      to={link.href}
      aria-label={link.text}
      primaryText={link.text}
      leftIcon={link.icon ? <link.icon /> : <LabelIcon />}
      onClick={onMenuClick}
      dense={dense}
      sidebarIsOpen={!open}
      classes={classesOverride}
      title="" /* Suppress tooltip on expand */
    />
  );
};

const ResourceMenuItem = ({ resource, open, ...props }) => {
  const translate = useTranslate();

  const { dense, onMenuClick } = props;

  const primaryText = translatedResourceName(resource, translate);

  return (
    <MenuItemLink
      key={resource.name}
      to={`/${resource.name}`}
      primaryText={primaryText}
      leftIcon={resource.icon ? <resource.icon /> : <DefaultIcon />}
      onClick={onMenuClick}
      dense={dense}
      sidebarIsOpen={open}
      title="" /* Suppress tooltip on expand */
    />
  );
};

const DashboardMenuItem = ({
  resource,
  ...props
}: {
  [x: string]: any;
  resource?: IMenuLink;
}) => {
  const translate = useTranslate();
  const primaryText = resource?.text
    ? resource.text
    : translate("ra.page.dashboard");

  return (
    <MenuItemLink
      key={1}
      to={"/"}
      exact
      primaryText={primaryText}
      leftIcon={resource?.icon ? <resource.icon /> : <DashboardIcon />}
      title="" /* Suppress tooltip on expand */
    />
  );
};

const isLink = (r: IMenuLink) => "type" in r && r.type === "link";
const isResourceLink = (r: IMenuLink) =>
  "type" in r && r.type === "resourcelink";
const isDashboardLink = (r: IMenuLink) => "type" in r && r.type === "dashboard";
const isResource = (r: IMenuLink) => !("type" in r);
const hasDividerAbove = (r: IMenuLink) => r?.options?.menuDividerAbove;
const hasDividerBelow = (r: IMenuLink) => r?.options?.menuDividerBelow;
const hasMenuSectionHeader = (r: IMenuLink, open) =>
  open && r?.options?.menuSectionHeader;

const Menu: FC<MenuProps> = ({
  links = [],
  hideResources = false,
  hideDashboard = false,
  ...props
}) => {
  const {
    classes: classesOverride,
    className,
    dense,
    hasDashboard,
    onMenuClick,
    logout,
    ...rest
  } = props;
  const classes = useStyles(props);
  const isXSmall = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("xs")
  );

  const translate = useTranslate();
  const open = useSelector((state: ReduxState) => state.admin.ui.sidebarOpen);
  const resources = useSelector(getResources, shallowEqual) as Array<any>;
  const userContext = useUserContext();

  const checkPermissions = (link: IMenuLink) => {
    if (!link.permission) {
      return true;
    } else if (link.permission.test(userContext.orgPermission?.permission!)) {
      return true;
    } else {
      return userContext.appPermissions.some((perm) =>
        link.permission?.test(perm)
      );
    }
  };

  /*
     Wait until permissions are loaded before selecting any links to be displayed
     which have a permission associated with them.
     TODO: Should we just not render anything in this case?
     */

  const permissableLinks: Array<IMenuLink> = links.filter((link) =>
    checkPermissions(link)
  );

  const forbiddenLinks: Array<IMenuLink> = links.filter(
    (link) => !checkPermissions(link)
  );

  /*
    Only render links to resources which contain lists, and have been loaded
    */
  const resourcesWithListView = resources.filter((r) => r.hasList);

  const linksWithLoadedResources = permissableLinks.filter(
    (link) => !isResourceLink(link) || resourcesWithListView.find((r) => r.name)
  );

  /*
    Filter out any remaining resources that have a separate link to them in the list view, as
    well as those for which there is no permission to view
    */
  const remainingResourceLinks =
    (!hideResources &&
      resourcesWithListView.filter(
        (r) =>
          !linksWithLoadedResources.find(
            (link) => isResourceLink(link) && r.name.match(link.resourceMatch)
          ) &&
          !forbiddenLinks.find(
            (link) => isResourceLink(link) && r.name.match(link.resourceMatch)
          )
      )) ||
    [];

  /*
    Concatenate displayable links, and any remaining resources,
    to create the full list of links.
    */
  const resourcesAndLinks = linksWithLoadedResources.concat(
    remainingResourceLinks
  );

  /*
   Determine whether to display the default dashboard (which is normally at the top),
   or elsewhere in the list order
   */
  const showDefaultDashboard =
    !hideDashboard && !permissableLinks.find((link) => isDashboardLink(link));

  /* Used to force redraw on navigation */
  useSelector((state: ReduxState) => state.router.location.pathname);

  return (
    <div
      className={classnames(
        classes.main,
        {
          [classes.open]: open,
          [classes.closed]: !open,
        },
        className
      )}
      {...rest}
    >
      {showDefaultDashboard && <DashboardMenuItem open={open} />}
      {resourcesAndLinks.map((r: any, i: number) => {
        return (
          <div key={i}>
            {hasDividerAbove(r) && <Divider variant="middle" />}
            {hasMenuSectionHeader(r, open) && (
              <Typography
                className={classes.menuSectionHeader}
                color="textSecondary"
                display="block"
                variant="caption"
              >
                {translate(r.options.menuSectionHeader)}
              </Typography>
            )}
            {isLink(r) && <ExternalLink link={r} open={open} />}
            {isDashboardLink(r) && (
              <DashboardMenuItem resource={r} open={open} />
            )}
            {isResourceLink(r) && (
              <ResourceMenuItem
                resource={resources.find((res) =>
                  res.name.match(r.resourceMatch)
                )}
                open={open}
              />
            )}
            {isResource(r) && <ResourceMenuItem resource={r} open={open} />}
            {hasDividerBelow(r) && <Divider variant="middle" />}
          </div>
        );
      })}
      {isXSmall && logout}
    </div>
  );
};

export default Menu;
